import type { CSSProperties } from '@linaria/core';
import { memo, useCallback } from 'react';
import type { Settings } from '../../../../types/state';
import { SettingsType } from '../../../constants/enums';
import { getAnimationsEnabledfromUeopt } from '../../../mfcLogic/settings';
import { addSearchHistoryItem } from '../../../state/state.search';
import { trackAnalyticsEvent } from '../../../utilities/analytics';
import { getModelColor } from '../../../utilities/colors';
import { useSearch } from '../../Hooks/useSearch';
import { useUserExists, useUserSelector } from '../../Hooks/useUser';
import { StyledContainer, StyledDetailsContainer, StyledMediaContainer, StyledModelName, StyledRoot, StyledTopic } from '../../common/SearchResultStyles';
import CounterViewers from '../CounterViewers';
import Header from '../Profile/Header';
import HighlightedMatch from '../Search/HighlightedMatch';
import "./SearchResult.linaria.module.css!=!./SearchResult?./SearchResult.linaria.module.css?css=H4sIAAAAAAAAA9NLLMmNN8+JN0zNrTLLMahOzs/JL7IqSyzS0NUFszWta7kAg6Pf5yUAAAA="
const stylesStyledModelName = "atm_7l_1emz6l0";
type SearchResultProps = {
  id: number;
  darkMode: boolean;
  isMobile: boolean;
  onOpenMenu: (slug: string, options?: any) => void;
  onOpenRoom: (slug: string) => void;
  onTagClick?: (tag: string) => void;
  searchString: string;
  settings: Settings[SettingsType.DEFAULT];
  cellWidth?: number;
  source: string;
  /** every result list the model matched - a combined search can match more than one */
  sources?: string[];
};
const SearchResult = memo<SearchResultProps>(props => {
  const {
    id,
    darkMode,
    onOpenMenu,
    onOpenRoom,
    searchString,
    settings,
    source,
    sources
  } = props;
  const resolvedSources = sources || (source ? [source] : []);
  const matchesName = resolvedSources.includes('name');
  const matchesTopic = resolvedSources.includes('topic');
  const {
    query
  } = useSearch();
  const {
    search: searchQuery
  } = query;
  const [loading, exists] = useUserExists(id);
  const {
    animatePreviews,
    display
  } = settings;
  const staticImage = !getAnimationsEnabledfromUeopt(animatePreviews);
  const isBroadcasting = useUserSelector(id, 'isBroadcasting');
  const isOffline = useUserSelector(id, 'isOffline');
  const isModel = useUserSelector(id, 'isModel');
  const is16x9 = useUserSelector(id, 'is16x9');
  const hasShare = useUserSelector(id, 'hasShare');
  const viewers = useUserSelector(id, 'viewers');
  const color = useUserSelector(id, 'color');
  const topic = useUserSelector(id, 'topic');
  const name = useUserSelector(id, 'name');
  const slug = useUserSelector(id, 'slug');
  const modelColor = getModelColor(color, darkMode);
  const onClickOpenMenu = useCallback(() => {
    trackAnalyticsEvent({
      action: `SearchResult_ClickThrough`,
      data: {
        openMenu: true
      }
    });
    onOpenMenu(slug);
    addSearchHistoryItem(searchQuery);
  }, [onOpenMenu, searchQuery, slug]);
  const onOpenContextMenu = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    trackAnalyticsEvent({
      action: `SearchResult_ClickThrough`,
      data: {
        openMenu: true,
        contextMenu: true
      }
    });
    onOpenMenu(slug);
  }, [name]);
  const onClickOpenRoom = useCallback(() => {
    trackAnalyticsEvent({
      action: `SearchResult_ClickThrough`,
      data: {
        openRoom: true
      }
    });
    onOpenRoom(slug);
    addSearchHistoryItem(searchQuery);
  }, [onOpenRoom, searchQuery, slug]);
  if (loading || exists === false) {
    return null;
  }
  return <StyledRoot data-testid="Search__Result__Container" onContextMenu={onOpenContextMenu}>
            <StyledContainer>
                <StyledMediaContainer>
                    <Header onClickAvatar={onClickOpenMenu} onClickPreview={onClickOpenRoom} broadcasting={isBroadcasting} displayMode={display} disableBadge={true} staticImage={staticImage} isMobile={false} offline={isOffline} showInfo={false} hasShare={hasShare} is16x9={is16x9} small={true} slug={slug} name={name} id={id} />
                </StyledMediaContainer>
                <StyledDetailsContainer data-testid="Search__Result" onClick={onClickOpenRoom}>
                    <StyledModelName style={{
          '--color': modelColor
        } as CSSProperties} data-testid="Search__Result__Name" className={stylesStyledModelName}>
                        {matchesName ? <HighlightedMatch source={name} searchString={searchString} /> : <>{name}</>}
                    </StyledModelName>
                    {isModel && !isOffline && <CounterViewers viewers={viewers} size={0.6} />}
                    <StyledTopic data-testid="Result__Topic" className={'mfc-age-gate'}>
                        {matchesTopic ? <HighlightedMatch source={topic} searchString={searchString} /> : <div>{topic}</div>}
                    </StyledTopic>
                </StyledDetailsContainer>
            </StyledContainer>
        </StyledRoot>;
});
export default SearchResult;